import { useMemo } from 'react'

import { Card, DescriptionList } from '@circlefin/components'
import { useDate, useMoney } from '@circlefin/hooks'
import { Currency, type NetRedemptionFeePayment } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

export interface NetRedemptionFeeCardProps {
  /**
   * Net Redemption Fee Payment Object.
   */
  payment: NetRedemptionFeePayment
}

export const NetRedemptionFeeCard: React.FC<NetRedemptionFeeCardProps> = ({
  payment,
}) => {
  const { t } = useTranslation('payments')
  const { date, dateNumeric: formatDate, toLuxonDateTime } = useDate()
  const { money } = useMoney()

  const timezone = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (payment.amount.currency) {
      case Currency.USDC:
        return 'America/New_York'
      case Currency.EURC:
        return 'Europe/Paris'
      default:
        // This should never happen, but is here to prevent
        // an adverse effect on the user experience.
        // An error message will be triggered at the API level
        // to notify the team.
        return 'UTC'
    }
  }, [payment.amount.currency])

  const { startDate, endDate } = useMemo(() => {
    const date = toLuxonDateTime(new Date(payment.createDate)).setZone(timezone)

    const lastDayOfPreviousMonth = date.minus({ months: 1 }).endOf('month')
    const firstDayOfPreviousMonth = lastDayOfPreviousMonth.startOf('month')

    return {
      startDate: firstDayOfPreviousMonth.toJSDate(),
      endDate: lastDayOfPreviousMonth.toJSDate(),
    }
  }, [payment.createDate, timezone, toLuxonDateTime])

  return (
    <Card>
      <Card.Content className="pb-0">
        <h2 className="pb-4 pl-4 type-h-title-lg">
          {t('redemptionFeeDetail.card.title')}
        </h2>

        <DescriptionList className="pl-4" density="loose">
          {/* Transaction Type */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.card.type')}
          </DescriptionList.Label>
          <DescriptionList.Description>
            <p className="type-body-base-bold">
              {t('redemptionFeeDetail.card.redemptionFee')}
            </p>
          </DescriptionList.Description>

          {/* Status */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.card.status')}
          </DescriptionList.Label>
          <DescriptionList.Description className="flex flex-col gap-1">
            <p className="type-body-base-bold">
              {t(`redemptionFeeDetail.card.statusLabels.${payment.status}`)}
            </p>
            <p className="type-body-xs">
              {formatDate(payment.updateDate, undefined, timezone)}
            </p>
          </DescriptionList.Description>

          {/* Amount */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.card.amount')}
          </DescriptionList.Label>
          <DescriptionList.Description>
            <p className="type-body-base-bold">
              {money({
                number: payment.amount.amount,
                variant: payment.amount.currency,
                options: {
                  symbol: true,
                  signDisplay: false,
                },
              })}
            </p>
          </DescriptionList.Description>

          {/* Billing Cycle */}
          <DescriptionList.Label className="w-64">
            {t('redemptionFeeDetail.card.billingCycle')}
          </DescriptionList.Label>
          <DescriptionList.Description>
            <p className="flex gap-1 type-body-base-bold">
              <span>{date(startDate, 'dateWithShortMonth', timezone)}</span>
              <span>-</span>
              <span>{date(endDate, 'dateWithShortMonth', timezone)}</span>
            </p>
          </DescriptionList.Description>
        </DescriptionList>
      </Card.Content>
    </Card>
  )
}
